import { supabase } from './supabase';

// =============================================================================
// Martwa sesja — token, który przeglądarka ma, a serwer auth już nie zna.
//
// Zmierzone 13.09.2026 (docs/02-ARCHITECTURE/SESJA_POWLOKI_I_2FA.md §1):
// desktop trzymał JWT, PostgREST go przyjmował (podpis i data ważności się
// zgadzają), więc panel ładował dane — a każda usługa, która pyta serwer auth
// (`auth.getUser`: nexus-core, websocket-gateway), dostawała
// `session_not_found`. Skutek dla człowieka: „Unauthorized" pod każdym
// przyciskiem wtyczki i „Offline" na karcie NEXUS, bez końca, bo
// `getSession()` oddaje token z pamięci i nikt go nie weryfikuje. Serwer auth
// dostał w ciągu dziesięciu minut 236 żądań `/user` z tym samym werdyktem.
//
// Reguła: odmowa 401 z usługi platformy jest PYTANIEM o sesję, nie komunikatem
// do wyświetlenia. Odpowiedź daje jedno wywołanie `auth.getUser()`:
// martwa → lokalne wylogowanie z powodem dla ekranu logowania; żywa → 401 jest
// sprawą tej jednej trasy (uprawnienia, wygaśnięcie w drodze) i wraca do
// wołającego. Wywołanie jest single-flight z odstępem, żeby dziesięć
// równoległych 401 nie zamieniło się w dziesięć `/user`.
// =============================================================================

/** Werdykt o sesji po pytaniu do serwera auth. */
export type WerdyktSesji = 'zywa' | 'martwa' | 'nieznany';

/**
 * Kody GoTrue, które znaczą „tej sesji już nie ma" — nie „chwilowa awaria".
 * `session_not_found` (403 na `/user`): wiersz `auth.sessions` usunięty
 * (wylogowanie globalne z innego urządzenia, limit sesji, kasacja konta).
 * `refresh_token_not_found` / `refresh_token_already_used` (400 na `/token`):
 * odświeżenie tym tokenem nie wróci. `user_not_found` / `user_banned`: konta nie ma
 * albo jest zablokowane. `bad_jwt`: token nie jest z tego projektu.
 */
export const KODY_MARTWEJ_SESJI = [
  'session_not_found',
  'session_expired',
  'refresh_token_not_found',
  'refresh_token_already_used',
  'user_not_found',
  'user_banned',
  'bad_jwt',
] as const;

/** Klucz w `sessionStorage`, który ekran logowania czyta i kasuje. */
export const KLUCZ_POWODU_WYLOGOWANIA = 'insolitum.powod-wylogowania';

export type PowodWylogowania = 'martwa_sesja';

/** Minimalny odstęp między dwoma pytaniami do serwera auth o tę samą sesję. */
export const ODSTEP_POTWIERDZENIA_MS = 15_000;

interface BladAuthKsztalt {
  code?: string | null;
  status?: number | null;
  message?: string;
}

/**
 * Czy błąd z GoTrue znaczy martwą sesję. Rozpoznanie po KODZIE, nie po
 * treści — treść jest tłumaczona i zmienia się między wersjami auth-js;
 * komunikaty bez kodu (starsze wersje) czytamy po znanych zdaniach.
 */
export function czyBladMartwejSesji(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as BladAuthKsztalt;
  if (e.code && (KODY_MARTWEJ_SESJI as readonly string[]).includes(e.code)) return true;
  const m = e.message ?? '';
  return (
    m.includes('Session not found') ||
    m.includes('Refresh Token Not Found') ||
    m.includes('Invalid Refresh Token') ||
    m.includes('User from sub claim in JWT does not exist')
  );
}

/**
 * Werdykt z odpowiedzi `auth.getUser()`. Błąd sieci albo 5xx to `nieznany` —
 * z niewiedzy nie wylogowujemy nikogo (ten sam kształt co `resolveAccess`).
 */
export function werdyktZOdpowiedzi(odpowiedz: {
  data: { user: unknown | null };
  error: unknown | null;
}): WerdyktSesji {
  if (odpowiedz.error) return czyBladMartwejSesji(odpowiedz.error) ? 'martwa' : 'nieznany';
  return odpowiedz.data.user ? 'zywa' : 'martwa';
}

interface Zegar {
  teraz: () => number;
}

interface KlientAuth {
  auth: {
    getUser: () => Promise<{ data: { user: unknown | null }; error: unknown | null }>;
    signOut: (opts: { scope: 'local' }) => Promise<{ error: unknown | null }>;
  };
}

/**
 * Stan single-flight. Trzymany w module, bo pytanie dotyczy JEDNEJ sesji
 * przeglądarki niezależnie od tego, która trasa je zadała.
 */
const stan: {
  wTrakcie: Promise<WerdyktSesji> | null;
  ostatnie: number;
  ostatniWerdykt: WerdyktSesji | null;
} = { wTrakcie: null, ostatnie: 0, ostatniWerdykt: null };

/**
 * Pyta serwer auth, czy sesja żyje. Współbieżne wywołania dzielą jedno
 * żądanie; kolejne w ciągu `ODSTEP_POTWIERDZENIA_MS` dostają ostatni werdykt
 * bez żądania.
 */
export async function potwierdzSesje(
  klient: KlientAuth = supabase as unknown as KlientAuth,
  zegar: Zegar = { teraz: () => Date.now() }
): Promise<WerdyktSesji> {
  if (stan.wTrakcie) return stan.wTrakcie;
  const teraz = zegar.teraz();
  if (stan.ostatniWerdykt && teraz - stan.ostatnie < ODSTEP_POTWIERDZENIA_MS) {
    return stan.ostatniWerdykt;
  }
  stan.wTrakcie = (async () => {
    try {
      const odpowiedz = await klient.auth.getUser();
      return werdyktZOdpowiedzi(odpowiedz);
    } catch (err) {
      return czyBladMartwejSesji(err) ? 'martwa' : 'nieznany';
    }
  })();
  try {
    const werdykt = await stan.wTrakcie;
    stan.ostatnie = zegar.teraz();
    stan.ostatniWerdykt = werdykt;
    return werdykt;
  } finally {
    stan.wTrakcie = null;
  }
}

/**
 * Kończy martwą sesję PO STRONIE TEJ PRZEGLĄDARKI: `scope: 'local'`, bo
 * globalne wylogowanie na sesji, której serwer nie zna, wraca 403 (zmierzone:
 * pięć takich prób z jednego telefonu w siedem minut). SIGNED_OUT z auth-js
 * uruchamia `runSignOutSequence` w AuthProvider; powód ląduje w
 * `sessionStorage`, skąd ekran logowania robi z niego jedno zdanie.
 */
export async function zakonczMartwaSesje(
  klient: KlientAuth = supabase as unknown as KlientAuth,
  magazyn: Pick<Storage, 'setItem'> | null = typeof sessionStorage === 'undefined'
    ? null
    : sessionStorage
): Promise<void> {
  try {
    magazyn?.setItem(KLUCZ_POWODU_WYLOGOWANIA, 'martwa_sesja' satisfies PowodWylogowania);
  } catch {
    /* prywatny tryb — zdanie na ekranie logowania po prostu nie padnie */
  }
  await klient.auth.signOut({ scope: 'local' });
}

/**
 * Jedyne wejście dla konsumentów: „dostałem 401 z usługi platformy — czy to
 * jeszcze moja sesja?". Zwraca `true`, gdy sesja była martwa i została
 * zakończona (wołający ma przestać pokazywać własny błąd), `false` — gdy 401
 * jest sprawą tej trasy.
 */
export async function poOdmowie401(
  zrodlo: string,
  klient: KlientAuth = supabase as unknown as KlientAuth
): Promise<boolean> {
  const werdykt = await potwierdzSesje(klient);
  if (werdykt !== 'martwa') return false;
  console.warn(`Sesja zakończona po stronie serwera auth — odmowa z: ${zrodlo}`);
  await zakonczMartwaSesje(klient);
  return true;
}

/** Odczyt-i-kasacja powodu dla ekranu logowania. */
export function pobierzPowodWylogowania(
  magazyn: Pick<Storage, 'getItem' | 'removeItem'> | null = typeof sessionStorage === 'undefined'
    ? null
    : sessionStorage
): PowodWylogowania | null {
  try {
    const powod = magazyn?.getItem(KLUCZ_POWODU_WYLOGOWANIA);
    if (powod) magazyn?.removeItem(KLUCZ_POWODU_WYLOGOWANIA);
    return powod === 'martwa_sesja' ? powod : null;
  } catch {
    return null;
  }
}

/** Wyłącznie dla testów: zerowanie single-flight między przypadkami. */
export const __testing = {
  wyzeruj(): void {
    stan.wTrakcie = null;
    stan.ostatnie = 0;
    stan.ostatniWerdykt = null;
  },
};
